import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Filter,
  RefreshCw,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  getAllPromises,
  createPromise,
  updatePromiseStatus,
} from '../../../services/firestore/promiseService';
import type { PatientPromise, PromiseStatus, PromiseSource, CreatePromiseInput } from '../../../types/promise';

// ── Status config ──

const STATUS_CONFIG: Record<PromiseStatus, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800', Icon: Clock },
  kept: { label: 'Kept', className: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  broken: { label: 'Broken', className: 'bg-red-100 text-red-800', Icon: XCircle },
  expired: { label: 'Expired', className: 'bg-gray-100 text-gray-600', Icon: AlertTriangle },
};

const SOURCE_LABELS: Record<PromiseSource, string> = {
  checkout: 'Checkout',
  nurse_chat: 'Nurse Chat',
  manual: 'Manual',
  system: 'System',
};

type StatusFilter = PromiseStatus | 'all';
type SourceFilter = PromiseSource | 'all';

export function PromisesPage() {
  const [promises, setPromises] = useState<PatientPromise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── Load promises ──

  const loadPromises = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllPromises();
      setPromises(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load promises');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPromises(); }, [loadPromises]);

  // ── Stats ──

  const stats = useMemo(() => {
    const total = promises.length;
    const pending = promises.filter((p) => p.status === 'pending').length;
    const kept = promises.filter((p) => p.status === 'kept').length;
    const broken = promises.filter((p) => p.status === 'broken').length;
    const expired = promises.filter((p) => p.status === 'expired').length;
    return { total, pending, kept, broken, expired };
  }, [promises]);

  // ── Filter ──

  const filtered = useMemo(() => {
    let result = [...promises];

    if (statusFilter !== 'all') {
      result = result.filter((p) => p.status === statusFilter);
    }
    if (sourceFilter !== 'all') {
      result = result.filter((p) => p.source === sourceFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.promise.toLowerCase().includes(q) ||
          p.context.toLowerCase().includes(q) ||
          p.patientLabel.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q)
      );
    }

    return result;
  }, [promises, statusFilter, sourceFilter, searchQuery]);

  // ── Toggle expand ──

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Handle status update ──

  const handleStatusUpdate = async (id: string, status: PromiseStatus, resolution: string) => {
    try {
      await updatePromiseStatus(id, status, resolution);
      setPromises((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status, resolution, updatedAt: new Date().toISOString() } : p))
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update promise');
    }
  };

  // ── Handle create ──

  const handleCreate = async (input: CreatePromiseInput) => {
    try {
      const id = await createPromise(input);
      setShowCreateForm(false);
      // Optimistic insert
      const now = new Date().toISOString();
      setPromises((prev) => [
        {
          id,
          ...input,
          status: 'pending' as PromiseStatus,
          resolution: '',
          createdAt: now,
          updatedAt: now,
        },
        ...prev,
      ]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create promise');
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display text-text-primary">Patient Promises</h1>
          <p className="text-sm text-text-secondary mt-1">
            Live tracking of every commitment made to patients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPromises}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-bg-card border border-bg-tertiary rounded text-text-secondary hover:bg-bg-elevated transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-accent-light text-white rounded font-medium hover:bg-accent-light/90 transition-colors"
          >
            <Plus size={14} />
            New Promise
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total} color="text-text-primary" bg="bg-bg-elevated" />
        <StatCard label="Pending" value={stats.pending} color="text-amber-700" bg="bg-amber-50" alert={stats.pending > 0} />
        <StatCard label="Kept" value={stats.kept} color="text-emerald-700" bg="bg-emerald-50" />
        <StatCard label="Broken" value={stats.broken} color="text-red-700" bg="bg-red-50" alert={stats.broken > 0} />
        <StatCard label="Expired" value={stats.expired} color="text-gray-600" bg="bg-gray-50" />
      </div>

      {/* ── Filters ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-text-muted" />
          <span className="text-sm font-medium text-text-secondary">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="kept">Kept</option>
              <option value="broken">Broken</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="all">All Sources</option>
              <option value="checkout">Checkout</option>
              <option value="nurse_chat">Nurse Chat</option>
              <option value="manual">Manual</option>
              <option value="system">System</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Promise, patient, context..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Results count ── */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Showing {filtered.length} of {stats.total} promises
        </p>
        {(statusFilter !== 'all' || sourceFilter !== 'all' || searchQuery) && (
          <button
            onClick={() => { setStatusFilter('all'); setSourceFilter('all'); setSearchQuery(''); }}
            className="text-sm text-accent-light hover:underline transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-light" />
          <span className="ml-2 text-sm text-text-secondary">Loading promises...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Promise List ── */}
      {!loading && !error && (
        <div className="bg-bg-card border border-bg-tertiary rounded overflow-hidden">
          {/* Header */}
          <div className="hidden md:grid md:grid-cols-[1fr_140px_100px_100px_120px_40px] gap-3 px-4 py-3 bg-[#18181B] text-white text-xs font-medium uppercase tracking-wide">
            <div>Promise</div>
            <div>Patient</div>
            <div>Source</div>
            <div>Status</div>
            <div>Deadline</div>
            <div></div>
          </div>

          {filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-text-muted text-sm">
              {stats.total === 0
                ? 'No promises registered yet. Create one manually or complete a checkout.'
                : 'No promises match the current filters.'}
            </div>
          ) : (
            <div>
              {filtered.map((promise, idx) => (
                <PromiseRow
                  key={promise.id}
                  promise={promise}
                  isExpanded={expandedIds.has(promise.id)}
                  onToggle={() => toggleExpand(promise.id)}
                  onStatusUpdate={handleStatusUpdate}
                  isOdd={idx % 2 === 1}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Create Form Modal ── */}
      {showCreateForm && (
        <CreatePromiseModal
          onClose={() => setShowCreateForm(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

// ── Stat Card ──

function StatCard({ label, value, color, bg, alert }: {
  label: string; value: number; color: string; bg: string; alert?: boolean;
}) {
  return (
    <div className={`${bg} border border-bg-tertiary rounded p-4 ${alert ? 'ring-2 ring-red-300' : ''}`}>
      <p className="text-xs font-medium text-text-muted uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-semibold font-mono mt-1 ${color}`}>{value}</p>
    </div>
  );
}

// ── Promise Row ──

function PromiseRow({ promise, isExpanded, onToggle, onStatusUpdate, isOdd }: {
  promise: PatientPromise;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusUpdate: (id: string, status: PromiseStatus, resolution: string) => void;
  isOdd: boolean;
}) {
  const [resolving, setResolving] = useState<PromiseStatus | null>(null);
  const [resolution, setResolution] = useState('');

  const deadlineDate = new Date(promise.deadline);
  const isOverdue = promise.status === 'pending' && deadlineDate < new Date();
  const deadlineStr = deadlineDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const { label, className, Icon } = STATUS_CONFIG[promise.status];

  const handleResolve = (status: PromiseStatus) => {
    if (status === promise.status) return;
    setResolving(status);
    setResolution('');
  };

  const confirmResolve = () => {
    if (!resolving) return;
    onStatusUpdate(promise.id, resolving, resolution);
    setResolving(null);
    setResolution('');
  };

  return (
    <div className={isOdd ? 'bg-[#F8FAFC]' : 'bg-white'}>
      <button
        onClick={onToggle}
        className="w-full text-left grid grid-cols-1 md:grid-cols-[1fr_140px_100px_100px_120px_40px] gap-2 md:gap-3 px-4 py-3 hover:bg-bg-elevated/60 transition-colors items-center"
      >
        {/* Promise text */}
        <div className="text-sm text-text-primary leading-snug">
          <span className="md:hidden font-medium text-text-muted text-xs block mb-0.5">Promise:</span>
          {promise.promise}
        </div>

        {/* Patient */}
        <div className="text-xs text-text-secondary truncate" title={promise.patientLabel}>
          <span className="md:hidden font-medium text-text-muted mr-1">Patient:</span>
          {promise.patientLabel}
        </div>

        {/* Source */}
        <div className="text-xs text-text-secondary">
          <span className="md:hidden font-medium text-text-muted mr-1">Source:</span>
          {SOURCE_LABELS[promise.source]}
        </div>

        {/* Status */}
        <div>
          <span className="md:hidden font-medium text-text-muted text-xs mr-1">Status:</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${className}`}>
            <Icon size={12} />
            {label}
          </span>
        </div>

        {/* Deadline */}
        <div className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-text-secondary'}`}>
          <span className="md:hidden font-medium text-text-muted mr-1">Deadline:</span>
          {deadlineStr}
          {isOverdue && ' (overdue)'}
        </div>

        {/* Expand */}
        <div className="hidden md:flex justify-center">
          {isExpanded ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
        </div>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-bg-tertiary">
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Context</h4>
              <p className="text-sm text-text-primary">{promise.context}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">ID</h4>
              <p className="text-sm font-mono text-text-primary">{promise.id}</p>
            </div>
            {promise.resolution && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Resolution</h4>
                <p className="text-sm text-text-primary">{promise.resolution}</p>
              </div>
            )}
            {promise.metadata && Object.keys(promise.metadata).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Metadata</h4>
                <div className="space-y-1">
                  {Object.entries(promise.metadata).map(([k, v]) => (
                    <p key={k} className="text-xs text-text-secondary">
                      <span className="font-mono text-text-muted">{k}:</span> {v}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Created</h4>
              <p className="text-sm text-text-primary">{new Date(promise.createdAt).toLocaleString()}</p>
            </div>
          </div>

          {/* Status actions — only for pending promises */}
          {promise.status === 'pending' && (
            <div className="mt-4 pt-4 border-t border-bg-tertiary">
              {!resolving ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-text-muted mr-2">Resolve:</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResolve('kept'); }}
                    className="px-3 py-1.5 text-xs bg-emerald-100 text-emerald-800 rounded hover:bg-emerald-200 transition-colors font-medium"
                  >
                    Mark Kept
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResolve('broken'); }}
                    className="px-3 py-1.5 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors font-medium"
                  >
                    Mark Broken
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResolve('expired'); }}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors font-medium"
                  >
                    Mark Expired
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-text-muted">
                    Resolving as <span className="font-semibold">{resolving}</span> — add a note:
                  </p>
                  <input
                    type="text"
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder="Resolution details..."
                    className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmResolve(); }}
                      className="px-4 py-1.5 text-xs bg-accent-light text-white rounded font-medium hover:bg-accent-light/90 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setResolving(null); }}
                      className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Create Promise Modal ──

function CreatePromiseModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (input: CreatePromiseInput) => void;
}) {
  const [promise, setPromise] = useState('');
  const [context, setContext] = useState('');
  const [patientId, setPatientId] = useState('');
  const [patientLabel, setPatientLabel] = useState('');
  const [deadline, setDeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isValid = promise.trim() && context.trim() && patientId.trim() && deadline;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    await onCreate({
      promise: promise.trim(),
      context: context.trim(),
      patientId: patientId.trim(),
      patientLabel: (patientLabel.trim() || patientId.trim()),
      source: 'manual',
      deadline: new Date(deadline).toISOString(),
    });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-bg-card border border-bg-tertiary rounded-xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-display font-bold text-text-primary">Register a Promise</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-elevated text-text-muted">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Promise <span className="text-red-500">*</span>
            </label>
            <textarea
              value={promise}
              onChange={(e) => setPromise(e.target.value)}
              placeholder="What was promised to the patient?"
              rows={2}
              className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Context <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Where/when was this promise made?"
              className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Patient ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                placeholder="Email or session ID"
                className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={patientLabel}
                onChange={(e) => setPatientLabel(e.target.value)}
                placeholder="Optional label"
                className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Deadline <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              className="px-6 py-2 text-sm bg-accent-light text-white rounded font-medium hover:bg-accent-light/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Registering...' : 'Register Promise'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
